import { fireToast } from '@repo/ui/components/Toast';
import debounce from 'lodash/debounce';
import { create } from 'zustand';

import { GeneratedImage } from 'components/BackgroundReplacer/Gallery/types';
import { BackgroundGenerationOptions } from 'components/BackgroundReplacer/types';
import generatorsApi, {
  GeneratedContentStatus,
  ReplaceBackgroundResponse,
} from 'global/generatorsApi';
import {
  createStateWithInitialImage,
  createStateWithProgress,
  GenerateImageError,
  hasInitialImage,
  hasItemsAndPages,
  hasProductImage,
  imageCanBeEdited,
  RemoveBackgroundError,
  State,
} from 'stores/backgroundReplacerStore/types';
import {
  createGeneratedImage,
  defaultImageSize,
  generateImage,
  removeBackground,
  selectGeneratedContent,
} from 'stores/backgroundReplacerStore/utils';

import { tryDeleteImage, uploadImage } from '../../components/AiTools/utils';
import { getImageDimensions } from '../../global/utils/fileUtils';

const GENERATOR_LOOK_UP_INTERVAL_MS = 3000;
const PREDEFINED_STYLES = ['2', '7'];

export const useBackgroundReplacerStore = create<State>((set, get) => ({
  state: 'loading',
  initializedFor: null,
  itemsPerPage: 0,
  imagesTimeout: {},
  getEditedImage: null,
  presetLabels: {},
  updateItemsPerPage: (value: number): void => {
    set({ itemsPerPage: value });
  },
  initialize: async (
    itemsPerPage: number,
    userId: string,
    presetLabels: Record<string, string>
  ): Promise<void> => {
    if (get().state !== 'loading' || get().initializedFor === userId) {
      return;
    }
    set({
      initializedFor: userId,
      itemsPerPage: itemsPerPage,
      presetLabels: presetLabels,
    });

    if (userId) {
      const { items, pages } = await get()._selectGeneratedContent(
        0,
        itemsPerPage
      );
      set({ state: { items, pages } });

      get()._scheduleLoadingImagesCheck();
    }
  },
  reset: (): void => {
    const state = get().state;
    if (hasInitialImage(state)) {
      URL.revokeObjectURL(state.initialImage);
    }
    get()._clearUpdateIntervals();
    set({ state: 'loading', initializedFor: null });
  },
  updatePresetLabels: (value: Record<string, string>): void => {
    set({ presetLabels: value });
  },
  _selectGeneratedContent: async (
    page: number,
    itemsPerPage: number
  ): Promise<{
    items: GeneratedImage[];
    pages: { current: number; total: number } | null;
  }> => {
    set({ itemsPerPage: itemsPerPage });
    const state = get().state;
    const initialImage = hasInitialImage(state)
      ? state.initialImage
      : undefined;
    let selectedPage = page;
    if (selectedPage === null) {
      selectedPage =
        hasItemsAndPages(state) && state.pages ? state.pages.current : 0;
    }

    const generatedContent = await selectGeneratedContent(
      selectedPage,
      itemsPerPage,
      get().presetLabels,
      initialImage
    );
    if ('error' in generatedContent) {
      fireToast({
        label: generatedContent.error,
        error: true,
      });
      return { items: [], pages: null };
    }

    get()._clearUpdateIntervals();

    return { items: generatedContent.items, pages: generatedContent.pages };
  },
  _clearUpdateIntervals: (): void => {
    Object.values(get().imagesTimeout).forEach((x) => clearTimeout(x));
    set({ imagesTimeout: {} });
  },
  _updateGeneratedContent: debounce(
    async (value: number, itemsPerPage: number) => {
      let state = get().state;
      if (!hasItemsAndPages(state) || !state.pages) {
        return;
      }
      const { items, pages } = await get()._selectGeneratedContent(
        value,
        itemsPerPage
      );
      state = get().state;
      // check that the user is still logged in
      if (!hasItemsAndPages(state) || !state.pages) {
        return;
      }
      set({ state: { ...state, items, pages } });

      get()._scheduleLoadingImagesCheck();
    },
    500,
    { leading: false }
  ),
  selectPage: async (
    value: number | null,
    itemsPerPage: number
  ): Promise<void> => {
    const state = get().state;
    if (!hasItemsAndPages(state) || !state.pages) {
      return;
    }
    if (value) {
      set({
        state: {
          ...state,
          pages: {
            current: value,
            total: state.pages.total,
          },
        },
      });
    }

    await get()._updateGeneratedContent(
      value ?? state.pages.current,
      itemsPerPage
    );
  },
  _scheduleLoadingImagesCheck: (): void => {
    const _state = get().state;
    if (!hasItemsAndPages(_state)) {
      return;
    }
    _state.items.forEach((x) => {
      if (x.isLoading) {
        get()._updateImageState(x.id);
      }
    });
  },
  _updateImageState: (id: string): void => {
    const _imagesTimeout = get().imagesTimeout;
    if (!_imagesTimeout[id]) {
      _imagesTimeout[id] = setTimeout(async () => {
        const newResponse = await generatorsApi.checkGeneratedImage(id);
        get()._handleResponse(newResponse);
      }, GENERATOR_LOOK_UP_INTERVAL_MS);
    }
  },
  _handleResponse: (response: ReplaceBackgroundResponse): void => {
    const showError = (error: string): void => {
      fireToast({
        label: error,
        error: true,
      });
    };

    if ('error' in response) {
      showError(response.error);
      return;
    }

    const _imagesTimeout = get().imagesTimeout;
    switch (response.status) {
      case GeneratedContentStatus.Failed:
        showError('Failed to generate Image');
        return;
      case GeneratedContentStatus.Started:
        // look it up again
        delete _imagesTimeout[response.id];
        set({ imagesTimeout: _imagesTimeout });
        get()._updateImageState(response.id);
        return;
      case GeneratedContentStatus.Complete:
        if (response.objectName) {
          get()._updateImage(response);
        } else {
          showError('Failed to load Image. Reload the page');
        }
        return;
      default: {
        // @ts-expect-error: exhaustiveCheck
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const exhaustiveCheck: never = response.status;
        throw new Error(
          `Unexpected Generated Content Status: ${response.status}`
        );
      }
    }
  },
  _updateImage: (
    response: ReplaceBackgroundResponse & { success: true }
  ): void => {
    const state = get().state;
    if (!hasItemsAndPages(state)) {
      return;
    }

    const newItem = createGeneratedImage(
      {
        id: response.id,
        status: response.status,
        objectName: response.objectName,
        loadingImage: hasInitialImage(state) ? state.initialImage : undefined,
      },
      get().presetLabels
    );

    if (!newItem) {
      return;
    }

    let images = state.items;
    const index = images.findIndex((x) => x.id === response.id);
    if (index >= 0) {
      images = images.map((x, i) => {
        if (i !== index) {
          return x;
        }

        return {
          ...newItem,
          label: x.label ?? newItem.label,
          prompt: newItem.prompt ?? x.prompt,
          negativePrompt: newItem.negativePrompt ?? x.negativePrompt,
        };
      });
    }

    set({ state: { ...state, items: images } });
  },
  selectImage: async (
    file: File,
    replace: boolean
  ): Promise<{ error?: RemoveBackgroundError }> => {
    const state = get().state;
    if (!hasItemsAndPages(state)) {
      return {};
    }
    const localUrl = URL.createObjectURL(file);
    const dimensions = await getImageDimensions(localUrl);

    set({
      state: createStateWithInitialImage(state, localUrl, dimensions),
    });

    const removeBackgroundResult = await removeBackground(file, dimensions);
    if (removeBackgroundResult.error) {
      fireToast({
        label: removeBackgroundResult.error,
        error: true,
        duration: 5000,
      });

      set((s) => {
        if (hasItemsAndPages(s.state)) {
          return {
            state: {
              items: s.state.items,
              pages: s.state.pages,
            },
          };
        }
        return { state: 'loading' };
      });
      URL.revokeObjectURL(localUrl);
      return { error: removeBackgroundResult.error };
    }

    set((s) => {
      if (!hasInitialImage(s.state)) {
        return { state: s.state };
      }
      return {
        state: {
          items: s.state.items,
          pages: s.state.pages,
          initialImage: s.state.initialImage,
          productImage: removeBackgroundResult.url!,
          editingIsReady: false,
          inProgress: !replace && s.state.items.length === 0,
        },
      };
    });
    return {};
  },
  generatePredefinedImages: async (
    limit: number,
    onGenerated: () => void
  ): Promise<void> => {
    let state = get().state;
    if (state === 'loading' || !imageCanBeEdited(state)) {
      return;
    }
    if (state.items.length > 0) {
      set({
        state: createStateWithProgress(state, false),
      });
      return;
    }

    const generationImageNumber =
      limit >= PREDEFINED_STYLES.length + 1 ? PREDEFINED_STYLES.length : 0;

    try {
      const waitFor = PREDEFINED_STYLES.slice(0, generationImageNumber).map(
        (style) => {
          return get().generateImage({ presetId: style });
        }
      );

      await Promise.all(waitFor);

      state = get().state;
      if (!imageCanBeEdited(state)) {
        return;
      }
      set({
        state: createStateWithProgress(state, false),
      });
      onGenerated();
    } catch (e) {
      fireToast({
        label: 'Failed to generate Image.',
        error: true,
      });
      console.error(e);
      set((s) => {
        if (hasItemsAndPages(s.state)) {
          return {
            state: {
              items: s.state.items,
              pages: s.state.pages,
            },
          };
        }
        return { state: 'loading' };
      });
    }
  },
  generateImage: async (
    options: BackgroundGenerationOptions
  ): Promise<{ error?: GenerateImageError }> => {
    const state = get().state;

    if (!hasProductImage(state)) {
      return { error: GenerateImageError.NO_SOURCE_IMAGE };
    }

    if (!imageCanBeEdited(state)) {
      return { error: GenerateImageError.NO_SOURCE_IMAGE };
    }

    set({
      state: createStateWithProgress(state, true),
    });

    const getEditedImage = get().getEditedImage;
    if (!getEditedImage) {
      return { error: GenerateImageError.NO_SOURCE_IMAGE };
    }
    const file = await getEditedImage();
    if (!file) {
      return { error: GenerateImageError.NO_SOURCE_IMAGE };
    }
    const { url: image, error } = await uploadImage(
      file,
      defaultImageSize,
      defaultImageSize,
      'png',
      true
    );
    if (error || !image) {
      return { error: GenerateImageError.UNKNOWN };
    }
    const response = await generateImage(image, options);

    if (!response.success) {
      set((s) => {
        if (imageCanBeEdited(s.state)) {
          return {
            state: createStateWithProgress(s.state, false),
          };
        }
        return { state: 'loading' };
      });
      if (response.error === 'UNSAFE_PROMPT') {
        return { error: GenerateImageError.UNSAFE_PROMPT };
      } else {
        fireToast({
          label: 'The image could not be generated. Try again.',
          error: true,
        });
        return { error: GenerateImageError.UNKNOWN };
      }
    }

    await get()._selectGeneratedContent(0, get().itemsPerPage);
    const { items, pages } = await get()._selectGeneratedContent(
      0,
      get().itemsPerPage
    );
    set((s) => {
      if (!imageCanBeEdited(s.state)) {
        return s;
      }
      return {
        state: {
          items: items,
          pages: pages,
          initialImage: s.state.initialImage,
          productImage: s.state.productImage,
          editingIsReady: s.state.editingIsReady,
          inProgress: false,
        },
      };
    });

    get()._scheduleLoadingImagesCheck();

    return { error: undefined };
  },
  tryDeleteImage: async (id: string): Promise<boolean> => {
    const state = get().state;
    if (!hasItemsAndPages(state) || !state.items.find((x) => x.id === id)) {
      return true;
    }

    const { error } = await tryDeleteImage(id);

    if (error) {
      fireToast({
        label: error,
        error: true,
      });
      return false;
    }

    const { state: _state, itemsPerPage } = get();
    if (!hasItemsAndPages(_state)) {
      return true;
    }

    const { items, pages } = await get()._selectGeneratedContent(
      _state.pages?.current ?? 0,
      itemsPerPage
    );

    set((s) => {
      if (!hasItemsAndPages(s.state)) {
        return s;
      }
      return {
        state: {
          ...s.state,
          items: items,
          pages: pages,
        },
      };
    });

    get()._scheduleLoadingImagesCheck();

    fireToast({
      label: 'Successfully deleted!',
    });
    return true;
  },
  setCanBeEdited: (value: boolean): void => {
    set((s) => {
      if (!hasProductImage(s.state)) {
        return s;
      }
      return { state: { ...s.state, editingIsReady: value } };
    });
  },
  registerGetEditedImageHandler: (
    handler: () => Promise<File | null>
  ): void => {
    set({ getEditedImage: handler });
  },
}));
